"use client";
import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import QuestionCard from "../QuestionCard/QuestionCard";
import getAnswersCount from "../../../../utils/getAnswersCount";
import SuccessBanner from "../../MedicalQuestionsPage/SuccessBanner/SuccessBanner";
import "./QuestionsList.css";

export default function QuestionsList({
  items = [],
  onDelete,
  onEdit,
  currentUserId,
}) {
  const { t, i18n } = useTranslation();
  const [successOpen, setSuccessOpen] = useState(false);

  const handleDelete = useCallback(
    async (id, ownerId) => {
      await onDelete?.(id, ownerId);
    },
    [onDelete]
  );

  const handleEdit = useCallback(
    async (id, newText) => {
      await onEdit?.(id, newText);
      setSuccessOpen(true);
      window.scrollTo({
        top: Math.max(window.scrollY - 120, 0),
        behavior: "smooth",
      });
    },
    [onEdit]
  );

  const mappedItems = useMemo(() => {
    return (items || []).map((q) => {
      const firstName = q.user?.firstName || t("questions.list.fallbackUser");
      const lastName = q.user?.lastName || "";
      const fullName = q.user?.fullName ||`${firstName} ${lastName}`.trim();

      const formattedDate = q.date
        ? new Date(q.date).toLocaleString(i18n.language || "fr-FR", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
        : t("questions.list.unknownDate");

      const ownerId = q.user?.id || q.user?._id || q.userId;

      const canDelete =
        ownerId === currentUserId ||
        ownerId === currentUserId?._id ||
        ownerId === currentUserId?.id;

      return {
        id: q.id_post || q.postId || q._id || q.id,
        ownerId,
        status: q.status || "pending",
        author: fullName,
        postedAt: formattedDate,
        text: q.description || q.text || t("questions.list.emptyQuestion"),
        answersCount: getAnswersCount(q),
        ai_response: q.ai_response ?? null,
        responders: q.responders || [],
        attachments: q.photo ? [q.photo] : [],
        canDelete,
      };
    });
  }, [items, currentUserId, t, i18n.language]);


  return (
    <div className="ql-container">
      {successOpen && (
        <SuccessBanner
          open={true}
          message={t("questions.list.editSuccess")}
          onClose={() => setSuccessOpen(false)}
        />
      )}

      {mappedItems.map((q) => (
        <QuestionCard
          key={q.id}
          id={q.id}
          ownerId={q.ownerId}
          status={q.status}
          author={q.author}
          postedAt={q.postedAt}
          text={q.text}
          answersCount={q.answersCount}
          attachments={q.attachments}
          canDelete={q.canDelete}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onAnswers={() => {}}
          onTranslate={() => {}}
            aiResponseText={q.ai_response}
            currentUserId={currentUserId}
        />
      ))}
    </div>
  );
}
